import type { PumsemTable, PumsemTableRow } from '../model/pumsem.type';

/**
 * 표 본문 세로 병합 — 화면 렌더러와 표 편집기가 **같은 계산**을 쓴다.
 *
 * 병합은 행의 `labelSpan`(이름 칸)·`spans`(열 키 → 행 수)에 있다. "이 칸부터
 * 아래로 n행" 이고, 덮인 행에도 값이 같은 값으로 들어 있다(서버가 채운다).
 * 그래서 병합을 풀면 덮였던 칸이 그 값을 그대로 이어 받는다.
 *
 * 규칙(서버 `pumsem-table-span.util.ts` 와 같다): 표 아래로 넘치지 않고, 같은
 * 칸 축에서 두 병합이 겹치지 않는다.
 */

/** 이름 칸을 열 키와 같은 축으로 다루기 위한 키. 열 키(`c1` …)와 겹치지 않는다. */
export const LABEL_CELL = '@label';

/** 행마다 칸 키 → 몇 행을 차지하는가. 1 = 보통 칸, n>1 = 병합 시작, 0 = 위 병합에 덮임. */
export type SpanLayout = Record<string, number>[];

function sizeAt(row: PumsemTableRow, key: string): number {
  const size = key === LABEL_CELL ? row.labelSpan : row.spans?.[key];
  return size && size > 1 ? size : 1;
}

function withSize(row: PumsemTableRow, key: string, size: number): PumsemTableRow {
  if (key === LABEL_CELL) {
    const { labelSpan: _drop, ...rest } = row;
    return size > 1 ? { ...rest, labelSpan: size } : rest;
  }
  const spans = { ...(row.spans ?? {}) };
  if (size > 1) spans[key] = size;
  else delete spans[key];
  const { spans: _drop, ...rest } = row;
  return Object.keys(spans).length > 0 ? { ...rest, spans } : rest;
}

function keysOf(table: PumsemTable): string[] {
  return [LABEL_CELL, ...table.columns.map((column) => column.key)];
}

/** 렌더러가 `rowSpan` 을 그리고 덮인 칸을 건너뛰는 데 쓴다. 넘치는 병합은 표 끝에서 자른다. */
export function spanLayout(table: PumsemTable): SpanLayout {
  const layout: SpanLayout = table.rows.map(() => ({}));
  for (const key of keysOf(table)) {
    let index = 0;
    while (index < table.rows.length) {
      const size = Math.min(sizeAt(table.rows[index], key), table.rows.length - index);
      layout[index][key] = size;
      for (let below = 1; below < size; below += 1) layout[index + below][key] = 0;
      index += size;
    }
  }
  return layout;
}

/** 이 칸을 덮는 병합의 시작 행. 병합이 아니면 자기 자신이다. */
export function spanStart(table: PumsemTable, rowIndex: number, key: string): number {
  const layout = spanLayout(table);
  let start = rowIndex;
  while (start > 0 && layout[start][key] === 0) start -= 1;
  return start;
}

/** 칸 값(이름 칸이면 이름). */
export function cellValue(row: PumsemTableRow, key: string): string {
  return key === LABEL_CELL ? row.label : (row.values[key] ?? '');
}

function setCell(row: PumsemTableRow, key: string, value: string | null): PumsemTableRow {
  if (key === LABEL_CELL) return { ...row, label: value ?? '' };
  return { ...row, values: { ...row.values, [key]: value } };
}

/**
 * 아래 칸과 합친다. 아래 칸이 이미 병합이면 그 병합째 삼킨다 — 스프레드시트처럼
 * **위 칸의 값**이 남고, 덮인 칸들도 그 값으로 바뀐다.
 */
export function mergeDown(table: PumsemTable, rowIndex: number, key: string): PumsemTable {
  const layout = spanLayout(table);
  const start = spanStart(table, rowIndex, key);
  const end = start + layout[start][key];
  if (end >= table.rows.length) return table;
  const size = layout[start][key] + layout[end][key];
  const value = cellValue(table.rows[start], key);

  const rows = table.rows.map((row, index) => {
    if (index === start) return withSize(row, key, size);
    if (index === end) return setCell(withSize(row, key, 1), key, value || null);
    if (index > start && index < start + size) return setCell(row, key, value || null);
    return row;
  });
  return { ...table, rows };
}

/** 병합을 푼다. 덮였던 칸은 지금 값을 그대로 갖는다(이미 같은 값이 들어 있다). */
export function unmerge(table: PumsemTable, rowIndex: number, key: string): PumsemTable {
  const start = spanStart(table, rowIndex, key);
  return {
    ...table,
    rows: table.rows.map((row, index) => (index === start ? withSize(row, key, 1) : row)),
  };
}

/** 병합 칸의 값을 고친다 — 덮인 행들도 같은 값을 갖게 한다. */
export function setSpannedCell(
  table: PumsemTable,
  rowIndex: number,
  key: string,
  value: string | null,
): PumsemTable {
  const layout = spanLayout(table);
  const start = spanStart(table, rowIndex, key);
  const end = start + layout[start][key];
  return {
    ...table,
    rows: table.rows.map((row, index) =>
      index >= start && index < end ? setCell(row, key, value) : row,
    ),
  };
}

/**
 * 행을 지운다. 그 행이 병합의 **시작**이면 병합을 다음 행으로 넘기고, 병합에
 * **덮인** 행이면 병합을 한 줄 줄인다 — 그냥 지우면 아래 병합이 한 칸씩 밀려
 * 엉뚱한 칸을 덮는다.
 */
export function removeRow(table: PumsemTable, rowIndex: number): PumsemTable {
  const layout = spanLayout(table);
  let rows = [...table.rows];
  for (const key of keysOf(table)) {
    const start = spanStart(table, rowIndex, key);
    const size = layout[start][key];
    if (size <= 1) continue;
    if (start === rowIndex) {
      rows[rowIndex + 1] = withSize(rows[rowIndex + 1], key, size - 1);
    } else {
      rows[start] = withSize(rows[start], key, size - 1);
    }
  }
  rows = rows.filter((_, index) => index !== rowIndex);
  return { ...table, rows };
}

/** 열을 지울 때 그 열의 병합도 같이 지운다. */
export function dropColumnSpans(table: PumsemTable, key: string): PumsemTable {
  return { ...table, rows: table.rows.map((row) => withSize(row, key, 1)) };
}
